import {lineAnchorFrom, lineOrientationFrom} from '@github-ui/diffs/diff-line-helpers'
import type {DiffAnchor} from '@github-ui/diffs/types'
import type {MouseEvent} from 'react'
import {useCallback} from 'react'

import {ContentCell, EmptyCell, LineNumberCell} from './DiffLineTableCellParts'
import {MarkersDialogContextProvider} from '../contexts/MarkersDialogContext'
import {useDiffContext} from '../contexts/DiffContext'
import {useDiffLineContext} from '../contexts/DiffLineContext'
import {isEmptyDiffLine} from '../helpers/line-helpers'
import type {DiffLine} from '../types'

type HandleDiffRowClickFn = (
  event: React.MouseEvent<HTMLTableCellElement>,
  lineNumber: number,
  orientation: 'left' | 'right',
  shiftKey: boolean,
) => void

export const CodeDiffLine = ({
  handleDiffSideCellSelectionBlocking,
  handleDiffRowClick,
  lineAnchor,
  diffLineSummary,
  filePath,
  shouldShowCopilot,
}: {
  filePath: string
  lineAnchor: string
  handleDiffSideCellSelectionBlocking?: (diffSide: 'left' | 'right') => void
  handleDiffRowClick: HandleDiffRowClickFn
  diffLineSummary: JSX.Element | null
  shouldShowCopilot: boolean
}) => {
  const {diffLine, isLeftSide, isSplit} = useDiffLineContext()
  const line = diffLine as DiffLine
  const {viewerData} = useDiffContext()
  const {tabSizePreference} = viewerData

  /*
   * display the left cell for all lines in a unified view, and left lines of a split view
   * this cell will not contain a number in the unified view if the line is an addition
   */
  const showLeftNumber = line.type !== 'ADDITION'
  const showLeftNumberCell = !isSplit || isLeftSide
  const displayingEmptyLeftNumberCell = showLeftNumberCell && !showLeftNumber

  /*
   * display the right cell for all lines in a unified view, and right lines of a split view
   * this cell will not contain a number in the unified view if the line is a deletion
   */
  const showRightNumber = line.type !== 'DELETION'
  const showRightNumberCell = !isSplit || !isLeftSide
  const displayingEmptyRightNumberCell = showRightNumberCell && !showRightNumber

  const handleLeftDiffCellClick = useCallback(
    (event: MouseEvent<HTMLTableCellElement>) => {
      if (!line.left) return
      handleDiffRowClick(event, line.left, 'left', event.shiftKey)
    },
    [handleDiffRowClick, line.left],
  )

  const handleRightDiffCellClick = useCallback(
    (event: MouseEvent<HTMLTableCellElement>) => {
      if (!line.right) return
      handleDiffRowClick(event, line.right, 'right', event.shiftKey)
    },
    [handleDiffRowClick, line.right],
  )

  const handleRightDiffSideCellSelectionBlocking = useCallback(() => {
    if (!handleDiffSideCellSelectionBlocking) return
    const diffSideToBlock = 'left'
    handleDiffSideCellSelectionBlocking(diffSideToBlock)
  }, [handleDiffSideCellSelectionBlocking])

  const handleLeftDiffSideCellSelectionBlocking = useCallback(() => {
    if (!handleDiffSideCellSelectionBlocking) return
    const diffSideToBlock = 'right'
    handleDiffSideCellSelectionBlocking(diffSideToBlock)
  }, [handleDiffSideCellSelectionBlocking])

  return (
    <MarkersDialogContextProvider line={line}>
      {showLeftNumberCell && (
        <LineNumberCell
          shouldShowCopilot={shouldShowCopilot}
          columnIndex={0}
          filePath={filePath}
          // If this cell is empty, select the right-side (modified) row
          handleDiffCellClick={displayingEmptyLeftNumberCell ? handleRightDiffCellClick : handleLeftDiffCellClick}
          handleDiffSideCellSelectionBlocking={handleLeftDiffSideCellSelectionBlocking}
        >
          {showLeftNumber && line.left}
        </LineNumberCell>
      )}
      {showRightNumberCell && (
        <LineNumberCell
          shouldShowCopilot={shouldShowCopilot}
          columnIndex={isSplit ? 2 : 1}
          filePath={filePath}
          // If this cell is empty, select the left-side (original) row
          handleDiffCellClick={displayingEmptyRightNumberCell ? handleLeftDiffCellClick : handleRightDiffCellClick}
          handleDiffSideCellSelectionBlocking={handleRightDiffSideCellSelectionBlocking}
        >
          {showRightNumber && line.right}
        </LineNumberCell>
      )}
      <ContentCell
        shouldShowCopilot={shouldShowCopilot}
        columnIndex={isSplit ? (isLeftSide ? 1 : 3) : 2}
        diffLineSummary={diffLineSummary}
        filePath={filePath}
        handleDiffCellClick={isLeftSide ? handleLeftDiffCellClick : handleRightDiffCellClick}
        lineAnchor={lineAnchor}
        tabSize={tabSizePreference}
        handleDiffSideCellSelectionBlocking={
          isLeftSide ? handleLeftDiffSideCellSelectionBlocking : handleRightDiffSideCellSelectionBlocking
        }
      />
    </MarkersDialogContextProvider>
  )
}

interface DiffSideProps {
  fileAnchor: DiffAnchor
  filePath: string
  handleDiffRowClick: HandleDiffRowClickFn
  handleDiffSideCellSelectionBlocking: (diffSide: 'left' | 'right') => void
  diffLineSummary: JSX.Element | null
  shouldShowCopilot: boolean
}

export const DiffLineSide = ({
  fileAnchor,
  filePath,
  handleDiffRowClick,
  handleDiffSideCellSelectionBlocking,
  diffLineSummary,
  shouldShowCopilot,
}: DiffSideProps) => {
  const {diffLine, isLeftSide} = useDiffLineContext()
  if (isEmptyDiffLine(diffLine)) {
    return (
      <>
        <EmptyCell columnIndex={isLeftSide ? 0 : 2} />
        <EmptyCell columnIndex={isLeftSide ? 1 : 3} showRightBorder={isLeftSide} />
      </>
    )
  }

  const lineOrientation = isLeftSide ? 'left' : lineOrientationFrom(diffLine.type)
  const lineNumber = lineOrientation === 'left' ? diffLine.left : diffLine.right
  const lineAnchor = lineAnchorFrom(fileAnchor, lineOrientation, lineNumber ?? 0)

  switch (diffLine.type) {
    case 'HUNK':
      return null
    case 'ADDITION':
    case 'DELETION':
    case 'CONTEXT':
    case 'INJECTED_CONTEXT':
      return (
        <CodeDiffLine
          shouldShowCopilot={shouldShowCopilot}
          diffLineSummary={diffLineSummary}
          filePath={filePath}
          handleDiffRowClick={handleDiffRowClick}
          handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
          lineAnchor={lineAnchor}
        />
      )
    default:
      throw new Error(`cannot handle type ${diffLine.type}`)
  }
}

try{ CodeDiffLine.displayName ||= 'CodeDiffLine' } catch {}
try{ DiffLineSide.displayName ||= 'DiffLineSide' } catch {}